import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle, Clock, Mail, AlertTriangle } from 'lucide-react';
import { Alert } from '../../services/data';
import clsx from 'clsx';

interface AlertTimelineProps {
  alert: Alert;
}

interface TimelineEvent {
  timestamp: string;
  icon: React.ReactNode;
  iconColor: string;
  title: string;
  description?: string;
}

export default function AlertTimeline({ alert }: AlertTimelineProps) {
  const { t } = useTranslation();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('default', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  };

  const events: TimelineEvent[] = [];

  events.push({
    timestamp: alert.triggered_at,
    icon: <AlertCircle className="w-5 h-5" />,
    iconColor: alert.severity === 'critical' ? 'text-red-600 bg-red-100' : 'text-yellow-600 bg-yellow-100',
    title: t('alerts.timeline.triggered'),
    description: alert.message,
  });

  if (alert.notification_sent_at) {
    events.push({
      timestamp: alert.notification_sent_at,
      icon: <Mail className="w-5 h-5" />,
      iconColor: 'text-blue-600 bg-blue-100',
      title: t('alerts.timeline.notificationSent'),
    });
  }

  if (alert.escalation_sent_at) {
    events.push({
      timestamp: alert.escalation_sent_at,
      icon: <AlertTriangle className="w-5 h-5" />,
      iconColor: 'text-orange-600 bg-orange-100',
      title: t('alerts.timeline.escalationSent'),
    });
  }

  if (alert.acknowledged_at) {
    events.push({
      timestamp: alert.acknowledged_at,
      icon: <Clock className="w-5 h-5" />,
      iconColor: 'text-blue-600 bg-blue-100',
      title: t('alerts.timeline.acknowledged', { user: 'User' }),
      description: alert.acknowledge_note,
    });
  }

  if (alert.resolved_at) {
    events.push({
      timestamp: alert.resolved_at,
      icon: <CheckCircle className="w-5 h-5" />,
      iconColor: 'text-green-600 bg-green-100',
      title: t('alerts.timeline.resolved'),
    });
  }

  events.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">{t('alerts.timeline.title')}</h3>

      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

        <div className="space-y-6">
          {events.map((event, index) => (
            <div key={index} className="relative flex gap-4">
              <div className={clsx('flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center z-10', event.iconColor)}>
                {event.icon}
              </div>
              <div className="flex-1 pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900">{event.title}</h4>
                    {event.description && (
                      <p className="mt-1 text-sm text-gray-600">{event.description}</p>
                    )}
                  </div>
                  <time className="text-xs text-gray-500 whitespace-nowrap">
                    {formatDate(event.timestamp)}
                  </time>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
